import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Loader2, ArrowLeft, FileWarning } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getLocalizedText } from '../contexts/StoreSettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function StaticPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(`${API_URL}/api/settings/public/page/${slug}`);
        
        // Vérifier si la réponse est du JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          setError('Erreur de chargement - réponse invalide');
          setPage(null);
          return;
        }
        
        const data = await response.json();
        
        if (!data.found) {
          setError('Page non trouvée');
          setPage(null);
        } else {
          setPage(data.page);
        }
      } catch (err) {
        console.error('Error fetching page:', err);
        setError('Erreur de chargement');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPage();
    }
  }, [slug]);

  const title = page ? getLocalizedText(page.title, lang) : '';
  const content = page ? getLocalizedText(page.content, lang) : '';

  return (
    <div className="min-h-screen flex flex-col bg-mutagim-dark">
      <Header />
      
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-mutagim-red" />
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <FileWarning className="h-16 w-16 mx-auto mb-4 text-gray-500" />
              <h1 className="text-2xl font-bold text-white mb-2">Page non trouvée</h1>
              <p className="text-gray-400 mb-6">
                La page "/{slug}" n'existe pas ou a été désactivée.
              </p>
              <Button 
                onClick={() => navigate('/')}
                className="bg-mutagim-red hover:bg-red-700"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour à l'accueil
              </Button>
            </div>
          ) : page ? (
            <article className="space-y-6">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-sm text-gray-400">
                <button onClick={() => navigate('/')} className="hover:text-white">
                  Accueil
                </button>
                <span>/</span>
                <span className="text-white">{title}</span>
              </nav>
              
              {/* Title */}
              <h1 className="text-3xl md:text-4xl font-bold text-white">
                {title}
              </h1>
              
              {/* Content */}
              <div 
                className="prose prose-invert prose-lg max-w-none
                  prose-headings:text-white prose-headings:font-bold
                  prose-p:text-gray-300 prose-p:leading-relaxed
                  prose-a:text-mutagim-red prose-a:no-underline hover:prose-a:underline
                  prose-strong:text-white
                  prose-ul:text-gray-300 prose-ol:text-gray-300
                  prose-li:marker:text-mutagim-red
                  prose-hr:border-gray-700
                  prose-blockquote:border-mutagim-red prose-blockquote:text-gray-400"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </article>
          ) : null}
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default StaticPage;
